"use client";

import type { BaseComponentProps } from "@json-render/react";
import { useStateValue } from "@json-render/react";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  ReferenceLine,
  Scatter,
  ScatterChart,
  type TooltipContentProps,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AiChartLegendContent,
  AiChartTooltipSurface,
  ChartContainer,
  ChartTooltip,
  resolveAiLegendLayout,
} from "./ai-chart";
import {
  DEFAULT_EXPORT_FORMATS,
  EMPTY_VIZ_ROWS,
  formatPercentTick,
  formatValueTick,
  formatVizTooltipMetricValue,
  isRecord,
  normalizeVizExportFormats,
  resolveVizAxisLineProps,
  resolveVizAxisStyle,
  resolveVizAxisTickLineProps,
  resolveVizCartesianGridProps,
  resolveVizNeutralGuideLineProps,
  resolveVizPaletteMode,
  resolveVizReferenceLabelProps,
  resolveVizReferenceLines,
  resolveVizSemanticReferenceLineProps,
  resolveVizSeriesColor,
  toFiniteNumberOrNull,
  type VizAxisPreset,
  type VizPaletteMode,
  type VizTooltipMode,
} from "./chart-format";
import {
  buildVizDatasetFieldsPath,
  buildVizFieldMetaMap,
  isPercentFieldKey,
  parseVizDatasetFields,
  type VizDatasetFieldMeta,
} from "./dataset-fields";
import { formatVizTooltipDomainLabel } from "./date-format";
import {
  copyVizAsPng,
  downloadRowsAsCsv,
  downloadRowsAsJson,
  downloadVizAsPng,
} from "./export-utils";
import { resolveCategoryLabel, resolveDisplayLabel } from "./label-resolver";
import {
  areStringArraysEqual,
  reconcileVisibleSeriesKeys,
  resolveInitialVisibleSeriesKeys,
  toggleVisibleSeriesKeys,
} from "./series-visibility";
import { VizActionBar, VizExportMenu, VizSeriesMenu } from "./viz-actions";

export type ScatterPlotProps = BaseComponentProps<{
  dataPath: string;
  xKey: string;
  yKey: string;
  zKey?: string | null;
  seriesKey?: string | null;
  series?: Array<{ key: string; label?: string | null; color?: string | null }>;
  xLabel?: string | null;
  yLabel?: string | null;
  seriesLabels?: Record<string, string> | null;
  axisPreset?: VizAxisPreset | null;
  tooltipMode?: VizTooltipMode | null;
  paletteMode?: VizPaletteMode | null;
  referenceLines?: Array<{
    value: number;
    label?: string | null;
    color?: string | null;
  }> | null;
  allowSeriesToggle?: boolean | null;
  defaultVisibleSeriesKeys?: string[] | null;
  allowExport?: boolean | null;
  exportFormats?: string[] | null;
}>;

type ScatterPoint = Record<string, unknown>;
const MOBILE_SCATTER_CHART_HEIGHT = 304;
const DESKTOP_SCATTER_CHART_HEIGHT = 336;
type ScatterTooltipMetricEntry = {
  key: string;
  label: string;
};
type ScatterTooltipIdentity = {
  titleKey: string | null;
  contextKeys: string[];
};
type ScatterHoverKey = {
  seriesValue: string | null;
  payload: ScatterPoint;
  xValue: number;
  yValue: number;
};
type ScatterShapePoint = {
  cx?: number;
  cy?: number;
  width?: number;
  height?: number;
  size?: number;
  fill?: string;
  fillOpacity?: number;
  stroke?: string;
  strokeOpacity?: number;
  strokeWidth?: number;
};
type ScatterTooltipContentProps = Partial<
  TooltipContentProps<number | string, string | number>
> & {
  fieldMetaMap: Map<string, VizDatasetFieldMeta>;
  identity: ScatterTooltipIdentity;
  metricEntries: ScatterTooltipMetricEntry[];
  formatMetricValue: (value: unknown, key: string) => string;
  i18n: I18n;
};

function resolveSeriesValue(value: unknown, fallbackLabel: string): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : fallbackLabel;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return fallbackLabel;
}

function hasScatterIdentityValue(value: unknown): boolean {
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  return typeof value === "number" && Number.isFinite(value);
}

function normalizeScatterIdentityValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function resolveScatterIdentityRank(
  field: VizDatasetFieldMeta,
  seriesKey: string | null | undefined,
): number {
  if (field.name === seriesKey) {
    return 4;
  }

  switch (field.role) {
    case "dim":
      return 0;
    case "x":
      return 1;
    case "series":
      return 2;
    default:
      return 3;
  }
}

/**
 * 散布図 tooltip 用に、各点を最も自然に識別できる row-grain field を選ぶ。
 *
 * @remarks
 * scatter は x/y がどちらも metric なので、tooltip には「この点が何を表すか」を
 * 別の dimension から補う必要がある。series color に使っている field は title で
 * 重ねず、まず comparability を壊さない row identity を優先する。
 */
export function resolveScatterTooltipIdentity(args: {
  fields: VizDatasetFieldMeta[];
  rows: ScatterPoint[];
  xKey: string;
  yKey: string;
  zKey: string | null | undefined;
  seriesKey: string | null | undefined;
}): ScatterTooltipIdentity {
  const excludedKeys = new Set(
    [args.xKey, args.yKey, args.zKey].filter(
      (key): key is string => typeof key === "string" && key.length > 0,
    ),
  );

  const rankedCandidates = args.fields
    .filter((field) => !excludedKeys.has(field.name) && field.role !== "metric")
    .map((field, index) => {
      const distinctValues = new Set(
        args.rows
          .map((row) => normalizeScatterIdentityValue(row[field.name]))
          .filter((value): value is string => value !== null),
      );

      return {
        field,
        index,
        distinctCount: distinctValues.size,
        rank: resolveScatterIdentityRank(field, args.seriesKey),
      };
    })
    .filter((candidate) => candidate.distinctCount > 0)
    .sort((left, right) => {
      if (left.distinctCount !== right.distinctCount) {
        return right.distinctCount - left.distinctCount;
      }
      if (left.rank !== right.rank) {
        return left.rank - right.rank;
      }
      return left.index - right.index;
    });

  if (rankedCandidates.length === 0) {
    return {
      titleKey: null,
      contextKeys: [],
    };
  }

  return {
    titleKey: rankedCandidates[0]!.field.name,
    contextKeys: rankedCandidates
      .slice(1)
      .map((candidate) => candidate.field.name),
  };
}

function formatScatterTooltipFieldValue(args: {
  value: unknown;
  fieldMeta: VizDatasetFieldMeta | undefined;
  i18n: I18n;
}): string {
  if (!hasScatterIdentityValue(args.value)) {
    return "";
  }

  if (args.fieldMeta?.type === "date") {
    return formatVizTooltipDomainLabel(args.value, args.i18n);
  }

  if (typeof args.value === "string") {
    return resolveCategoryLabel({
      value: args.value,
      fieldMeta: args.fieldMeta,
      i18n: args.i18n,
    });
  }

  return formatVizTooltipDomainLabel(args.value, args.i18n);
}

function resolveScatterTooltipRow(
  payload: ScatterTooltipContentProps["payload"],
): ScatterPoint | null {
  const row = payload?.[0]?.payload;
  return isRecord(row) ? (row as ScatterPoint) : null;
}

function parseScatterShapePoint(value: unknown): ScatterShapePoint | null {
  return isRecord(value) ? (value as ScatterShapePoint) : null;
}

function resolveScatterShapePayload(
  point: ScatterShapePoint,
): ScatterPoint | null {
  return isRecord((point as Record<string, unknown>).payload)
    ? (((point as Record<string, unknown>).payload as ScatterPoint) ?? null)
    : null;
}

function isScatterHoverKeyEqual(
  left: ScatterHoverKey | null,
  right: ScatterHoverKey | null,
): boolean {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.seriesValue === right.seriesValue &&
    left.payload === right.payload &&
    left.xValue === right.xValue &&
    left.yValue === right.yValue
  );
}

/**
 * Scatter point の現在サイズから、UI 調整に使う描画半径を復元する。
 *
 * @remarks
 * Recharts の `size` は抽象値なので、そのままでは hover halo や custom shape の
 * 見た目を合わせづらい。既に計算済みの width/height を優先して実半径へ揃える。
 */
export function resolveScatterPointRadius(point: ScatterShapePoint): number {
  const widthRadius =
    typeof point.width === "number" && Number.isFinite(point.width)
      ? point.width / 2
      : null;
  const heightRadius =
    typeof point.height === "number" && Number.isFinite(point.height)
      ? point.height / 2
      : null;
  const derivedRadius = Math.max(widthRadius ?? 0, heightRadius ?? 0);

  if (derivedRadius > 0) {
    return derivedRadius;
  }

  if (typeof point.size === "number" && Number.isFinite(point.size)) {
    return Math.max(4, Math.sqrt(point.size) / 2);
  }

  return 5;
}

/**
 * scatter の通常ドットを、hover 時の halo と同じ半径計算で描画する。
 *
 * @remarks
 * default symbol のままだと active shape だけが別計算になり、hover 前後で
 * サイズ感が不連続に見える。通常・active の両方を同じ renderer で揃える。
 */
function renderScatterPointShape(props: unknown) {
  const point = parseScatterShapePoint(props);
  if (!point) {
    return <g />;
  }

  if (
    typeof point.cx !== "number" ||
    !Number.isFinite(point.cx) ||
    typeof point.cy !== "number" ||
    !Number.isFinite(point.cy)
  ) {
    return <g />;
  }

  return (
    <circle
      cx={point.cx}
      cy={point.cy}
      r={resolveScatterPointRadius(point)}
      fill={point.fill ?? "currentColor"}
      fillOpacity={point.fillOpacity ?? 1}
      stroke={point.stroke}
      strokeOpacity={point.strokeOpacity}
      strokeWidth={point.strokeWidth}
    />
  );
}

/**
 * hover 中の散布図ドットを、控えめな halo と少し大きい本体で強調する。
 *
 * @remarks
 * 座標読解は neutral guide line に任せ、点そのものの強調は halo へ閉じ込める。
 * こうしておくと、hover 対象の特定と数値の読み取りを両立しつつ、
 * scatter 特有の密集感も壊しにくい。
 */
function renderScatterHoveredPointShape(props: unknown) {
  const point = parseScatterShapePoint(props);
  if (!point) {
    return <g />;
  }

  if (
    typeof point.cx !== "number" ||
    !Number.isFinite(point.cx) ||
    typeof point.cy !== "number" ||
    !Number.isFinite(point.cy)
  ) {
    return <g />;
  }

  const radius = resolveScatterPointRadius(point);

  return (
    <g>
      <circle
        cx={point.cx}
        cy={point.cy}
        r={radius + 5}
        fill={point.fill ?? "currentColor"}
        fillOpacity={0.14}
      />
      <circle
        cx={point.cx}
        cy={point.cy}
        r={radius + 1.5}
        fill={point.fill ?? "currentColor"}
        fillOpacity={point.fillOpacity ?? 0.95}
        stroke={point.stroke}
        strokeOpacity={point.strokeOpacity}
        strokeWidth={(point.strokeWidth ?? 1.5) + 0.5}
      />
    </g>
  );
}

/**
 * scatter 専用 tooltip presenter。
 *
 * @remarks
 * scatter では 1 点の「正体」が見えないと相関や外れ値の読み取りが難しいため、
 * metric rows の前に row-grain title と補助コンテキストを必ず出せる専用 layout を使う。
 */
function ScatterTooltipContent({
  active,
  payload,
  fieldMetaMap,
  identity,
  metricEntries,
  formatMetricValue,
  i18n,
}: ScatterTooltipContentProps) {
  const row = useMemo(() => resolveScatterTooltipRow(payload), [payload]);

  const title = useMemo(() => {
    if (!row || !identity.titleKey) {
      return null;
    }

    const value = formatScatterTooltipFieldValue({
      value: row[identity.titleKey],
      fieldMeta: fieldMetaMap.get(identity.titleKey),
      i18n,
    });
    return value.length > 0 ? value : null;
  }, [fieldMetaMap, i18n, identity.titleKey, row]);

  const subtitle = useMemo(() => {
    if (!row || identity.contextKeys.length === 0) {
      return null;
    }

    const values = identity.contextKeys
      .map((key) =>
        formatScatterTooltipFieldValue({
          value: row[key],
          fieldMeta: fieldMetaMap.get(key),
          i18n,
        }),
      )
      .filter((value) => value.length > 0);

    return values.length > 0 ? values.join(" · ") : null;
  }, [fieldMetaMap, i18n, identity.contextKeys, row]);

  const metricRows = useMemo(() => {
    if (!row) {
      return [];
    }

    return metricEntries
      .map((entry) => {
        const rawValue = row[entry.key];
        if (rawValue === null || rawValue === undefined) {
          return null;
        }

        return {
          key: entry.key,
          label: entry.label,
          value: formatMetricValue(rawValue, entry.key),
        };
      })
      .filter((entry): entry is { key: string; label: string; value: string } =>
        Boolean(entry),
      );
  }, [formatMetricValue, metricEntries, row]);

  if (!active || !row || metricRows.length === 0) {
    return null;
  }

  return (
    <AiChartTooltipSurface>
      {title || subtitle ? (
        <div className="border-border/50 mb-2.5 grid gap-1 border-b pb-2">
          {title ? (
            <p className="whitespace-normal break-words text-[13px] font-semibold leading-tight text-foreground">
              {title}
            </p>
          ) : null}
          {subtitle ? (
            <p className="whitespace-normal break-words text-[11px] leading-tight text-muted-foreground">
              {subtitle}
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="grid gap-1.5">
        {metricRows.map((entry) => (
          <div
            key={entry.key}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-4"
          >
            <span className="whitespace-normal break-words text-[11px] leading-tight text-muted-foreground">
              {entry.label}
            </span>
            <span className="font-mono text-xs font-semibold tabular-nums text-foreground">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    </AiChartTooltipSurface>
  );
}

/**
 * 2-3 指標の分布傾向を確認する散布図を描画する。
 *
 * @remarks
 * 点群の重なりや軸目盛りが読みづらくなりやすいため、style preset を受けて
 * 配色・軸・ツールチップを一貫制御する。
 */
export function ScatterPlot({ props }: ScatterPlotProps) {
  const { i18n } = useLingui();
  const isMobile = useIsMobile();
  const {
    dataPath,
    xKey,
    yKey,
    zKey,
    seriesKey,
    series,
    xLabel,
    yLabel,
    seriesLabels,
    axisPreset,
    paletteMode,
    referenceLines,
    allowSeriesToggle,
    defaultVisibleSeriesKeys,
    allowExport,
    exportFormats,
  } = props;
  const resolvedPaletteMode = resolveVizPaletteMode(paletteMode);
  const fallbackSeriesLabel = i18n._(msg`Series`);
  const aggregateSeriesLabel = i18n._(msg`All`);
  const rawData = useStateValue<unknown>(dataPath);
  const fieldsPath = useMemo(
    () => buildVizDatasetFieldsPath(dataPath),
    [dataPath],
  );
  const rawFields = useStateValue<unknown>(fieldsPath);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const data = useMemo(() => {
    if (!Array.isArray(rawData)) return EMPTY_VIZ_ROWS;
    return rawData.filter(isRecord);
  }, [rawData]);

  const parsedFields = useMemo(
    () => parseVizDatasetFields(rawFields),
    [rawFields],
  );
  const metaByName = useMemo(
    () => buildVizFieldMetaMap(parsedFields),
    [parsedFields],
  );
  const plottedRows = useMemo(() => {
    return data.filter((row) => {
      return (
        toFiniteNumberOrNull(row[xKey]) !== null &&
        toFiniteNumberOrNull(row[yKey]) !== null
      );
    });
  }, [data, xKey, yKey]);
  const resolvedXAxisLabel = useMemo(
    () => resolveDisplayLabel(xLabel, metaByName.get(xKey)?.label, xKey),
    [metaByName, xKey, xLabel],
  );
  const resolvedYAxisLabel = useMemo(
    () => resolveDisplayLabel(yLabel, metaByName.get(yKey)?.label, yKey),
    [metaByName, yKey, yLabel],
  );
  const resolvedZAxisLabel = useMemo(() => {
    if (!zKey) {
      return null;
    }
    return resolveDisplayLabel(metaByName.get(zKey)?.label, zKey);
  }, [metaByName, zKey]);

  const groupedPoints = useMemo(() => {
    const groups = new Map<string, ScatterPoint[]>();
    for (const row of plottedRows) {
      const x = toFiniteNumberOrNull(row[xKey]);
      const y = toFiniteNumberOrNull(row[yKey]);
      if (x === null || y === null) continue;
      const z = zKey ? toFiniteNumberOrNull(row[zKey]) : null;
      const group = seriesKey
        ? resolveSeriesValue(row[seriesKey], fallbackSeriesLabel)
        : aggregateSeriesLabel;
      const points = groups.get(group) ?? [];
      const point: ScatterPoint = {
        ...row,
        [xKey]: x,
        [yKey]: y,
      };
      if (zKey) {
        point[zKey] = z;
      }
      if (seriesKey) {
        point[seriesKey] = group;
      }
      points.push({
        ...point,
      });
      groups.set(group, points);
    }
    return groups;
  }, [
    aggregateSeriesLabel,
    fallbackSeriesLabel,
    plottedRows,
    seriesKey,
    xKey,
    yKey,
    zKey,
  ]);

  const derivedSeries = useMemo(
    () =>
      [...groupedPoints.keys()].map((key) => ({
        key,
        label: key,
        color: null,
      })),
    [groupedPoints],
  );
  const sourceSeries = useMemo(() => {
    if (Array.isArray(series) && series.length > 0) {
      return series;
    }
    return derivedSeries;
  }, [derivedSeries, series]);
  const allSeriesKeys = useMemo(
    () => sourceSeries.map((entry) => entry.key),
    [sourceSeries],
  );

  const [visibleSeriesKeys, setVisibleSeriesKeys] = useState<string[]>(() =>
    resolveInitialVisibleSeriesKeys({
      allSeriesKeys,
      defaultVisibleSeriesKeys,
    }),
  );
  const [hoveredPointKey, setHoveredPointKey] =
    useState<ScatterHoverKey | null>(null);

  useEffect(() => {
    setVisibleSeriesKeys((current) => {
      const next = reconcileVisibleSeriesKeys({ current, allSeriesKeys });
      return areStringArraysEqual(next, current) ? current : next;
    });
  }, [allSeriesKeys]);

  const toggleSeriesVisibility = useCallback(
    (key: string, checked?: boolean) => {
      setVisibleSeriesKeys((current) => {
        const updated = toggleVisibleSeriesKeys({
          current,
          allSeriesKeys,
          key,
          checked,
        });
        return areStringArraysEqual(updated, current) ? current : updated;
      });
    },
    [allSeriesKeys],
  );
  const handlePointMouseLeave = useCallback(() => {
    setHoveredPointKey(null);
  }, []);
  const createPointMouseEnterHandler = useCallback(
    (seriesValue: string | null) => (entry: unknown) => {
      if (!isRecord(entry) || !isRecord(entry.payload)) {
        setHoveredPointKey(null);
        return;
      }

      const xValue = toFiniteNumberOrNull(entry.payload[xKey]);
      const yValue = toFiniteNumberOrNull(entry.payload[yKey]);
      if (xValue === null || yValue === null) {
        setHoveredPointKey(null);
        return;
      }

      const next = {
        seriesValue,
        payload: entry.payload as ScatterPoint,
        xValue,
        yValue,
      } satisfies ScatterHoverKey;

      setHoveredPointKey((current) =>
        isScatterHoverKeyEqual(current, next) ? current : next,
      );
    },
    [xKey, yKey],
  );

  const visibleSeriesKeySet = useMemo(
    () => new Set(visibleSeriesKeys),
    [visibleSeriesKeys],
  );
  const effectiveSeries = useMemo(() => {
    return sourceSeries.filter((entry) => visibleSeriesKeySet.has(entry.key));
  }, [sourceSeries, visibleSeriesKeySet]);

  const chartConfig = useMemo(() => {
    const axisEntries = [
      [
        xKey,
        {
          label: resolvedXAxisLabel,
        },
      ],
      [
        yKey,
        {
          label: resolvedYAxisLabel,
        },
      ],
      ...(zKey
        ? [
            [
              zKey,
              {
                label: resolvedZAxisLabel || zKey,
              },
            ] as const,
          ]
        : []),
    ] as const;

    const seriesEntries = sourceSeries.map((entry, index) => [
      entry.key,
      {
        label:
          resolveDisplayLabel(
            seriesLabels?.[entry.key],
            entry.label,
            entry.key,
          ) || entry.key,
        color: resolveVizSeriesColor({
          index,
          color: entry.color,
          paletteMode: resolvedPaletteMode,
        }),
      },
    ]);

    return Object.fromEntries([...axisEntries, ...seriesEntries]);
  }, [
    resolvedPaletteMode,
    resolvedXAxisLabel,
    resolvedYAxisLabel,
    resolvedZAxisLabel,
    seriesLabels,
    sourceSeries,
    xKey,
    yKey,
    zKey,
  ]);

  const percentFields = useMemo(() => {
    const fields: string[] = [];
    if (isPercentFieldKey(xKey, metaByName)) fields.push(xKey);
    if (isPercentFieldKey(yKey, metaByName)) fields.push(yKey);
    return fields;
  }, [metaByName, xKey, yKey]);
  const formatTooltipValue = useCallback(
    (value: unknown, key: string) =>
      formatVizTooltipMetricValue(value, {
        isPercent: percentFields.includes(key),
      }),
    [percentFields],
  );
  const tooltipIdentity = useMemo(
    () =>
      resolveScatterTooltipIdentity({
        fields: parsedFields,
        rows: plottedRows,
        xKey,
        yKey,
        zKey,
        seriesKey,
      }),
    [parsedFields, plottedRows, seriesKey, xKey, yKey, zKey],
  );
  const tooltipMetricEntries = useMemo(
    (): ScatterTooltipMetricEntry[] => [
      {
        key: xKey,
        label: resolvedXAxisLabel,
      },
      {
        key: yKey,
        label: resolvedYAxisLabel,
      },
      ...(zKey
        ? [
            {
              key: zKey,
              label: resolvedZAxisLabel || zKey,
            },
          ]
        : []),
    ],
    [
      resolvedXAxisLabel,
      resolvedYAxisLabel,
      resolvedZAxisLabel,
      xKey,
      yKey,
      zKey,
    ],
  );

  const referenceLineEntries = useMemo(
    () => resolveVizReferenceLines(referenceLines),
    [referenceLines],
  );

  const axisStyle = useMemo(
    () =>
      resolveVizAxisStyle({
        preset: axisPreset,
        isMobile,
      }),
    [axisPreset, isMobile],
  );
  const resolvedExportFormats = normalizeVizExportFormats(
    exportFormats,
    DEFAULT_EXPORT_FORMATS,
  );

  const handleExport = useCallback(
    async (format: "csv" | "json" | "png") => {
      const columns = [
        xKey,
        yKey,
        ...(zKey ? [zKey] : []),
        ...(seriesKey ? [seriesKey] : []),
      ];
      if (format === "csv") {
        downloadRowsAsCsv({ rows: data, columns, baseName: "scatter-plot" });
        return;
      }
      if (format === "json") {
        downloadRowsAsJson({ rows: data, columns, baseName: "scatter-plot" });
        return;
      }
      await downloadVizAsPng({
        element: containerRef.current,
        baseName: "scatter-plot",
      });
    },
    [data, seriesKey, xKey, yKey, zKey],
  );
  const handleCopyImage = useCallback(async () => {
    await copyVizAsPng({
      element: containerRef.current,
    });
  }, []);

  const legendEntries = useMemo(
    () =>
      sourceSeries.map((entry) => ({
        key: entry.key,
        label:
          resolveDisplayLabel(
            seriesLabels?.[entry.key],
            entry.label,
            entry.key,
          ) || entry.key,
      })),
    [seriesLabels, sourceSeries],
  );
  const legendPayload = useMemo(
    () =>
      legendEntries.map((entry) => ({
        dataKey: entry.key,
        value: entry.label,
        color: chartConfig[entry.key]?.color ?? "var(--chart-1)",
      })),
    [chartConfig, legendEntries],
  );
  const hasData = effectiveSeries.some((entry) => {
    const points = groupedPoints.get(entry.key);
    return Array.isArray(points) && points.length > 0;
  });
  const resolvedChartHeight = isMobile
    ? MOBILE_SCATTER_CHART_HEIGHT
    : DESKTOP_SCATTER_CHART_HEIGHT;
  const zAxisRange = useMemo<[number, number]>(
    () => (isMobile ? [49, 256] : [64, 324]),
    [isMobile],
  );
  const legendLayout = useMemo(
    () =>
      resolveAiLegendLayout({
        itemCount: legendPayload.length,
        isMobile,
      }),
    [isMobile, legendPayload.length],
  );
  return (
    <div ref={containerRef} className="w-full">
      {allowSeriesToggle !== false || allowExport !== false ? (
        <VizActionBar>
          {allowSeriesToggle !== false ? (
            <VizSeriesMenu
              series={legendEntries}
              visibleKeySet={visibleSeriesKeySet}
              onToggle={toggleSeriesVisibility}
            />
          ) : null}
          {allowExport !== false ? (
            <VizExportMenu
              formats={resolvedExportFormats}
              onExport={(format) => handleExport(format)}
              onCopyImage={handleCopyImage}
              disabled={!hasData}
            />
          ) : null}
        </VizActionBar>
      ) : null}
      {/* Toggleを無効化しても色と系列名の対応は必要なため、静的labelだけをplot上部へ残す。 */}
      {allowSeriesToggle === false && legendPayload.length > 1 ? (
        <AiChartLegendContent
          className={legendLayout.className}
          verticalAlign="top"
          payload={legendPayload}
          visibleKeySet={visibleSeriesKeySet}
        />
      ) : null}
      <ChartContainer
        config={chartConfig}
        className="w-full aspect-auto"
        style={{ height: `${resolvedChartHeight}px` }}
      >
        <ScatterChart
          margin={{
            top: 24,
            right: 24,
            bottom: resolvedXAxisLabel ? 34 : 18,
            left: resolvedYAxisLabel ? 18 : 10,
          }}
        >
          <CartesianGrid {...resolveVizCartesianGridProps({ axisStyle })} />
          <XAxis
            type="number"
            dataKey={xKey}
            name={resolvedXAxisLabel}
            tickFormatter={(value) =>
              percentFields.includes(xKey)
                ? formatPercentTick(value)
                : formatValueTick(value)
            }
            tickLine={resolveVizAxisTickLineProps()}
            axisLine={resolveVizAxisLineProps()}
            tickMargin={axisStyle.tickMargin}
            height={resolvedXAxisLabel ? 42 : 28}
            label={
              resolvedXAxisLabel
                ? {
                    value: resolvedXAxisLabel,
                    position: "insideBottom",
                    offset: -6,
                    style: {
                      textAnchor: "middle",
                      fontSize: 12,
                      fill: "hsl(var(--muted-foreground))",
                    },
                  }
                : undefined
            }
            tick={{ fontSize: axisStyle.tickFontSize }}
          />
          <YAxis
            type="number"
            dataKey={yKey}
            name={resolvedYAxisLabel}
            tickFormatter={(value) =>
              percentFields.includes(yKey)
                ? formatPercentTick(value)
                : formatValueTick(value)
            }
            label={
              resolvedYAxisLabel
                ? {
                    value: resolvedYAxisLabel,
                    angle: -90,
                    position: "insideLeft",
                    offset: -2,
                    style: {
                      textAnchor: "middle",
                      fontSize: 12,
                      fill: "hsl(var(--muted-foreground))",
                    },
                  }
                : undefined
            }
            width={axisStyle.yAxisWidth}
            tickLine={resolveVizAxisTickLineProps()}
            axisLine={resolveVizAxisLineProps()}
            tickMargin={axisStyle.tickMargin}
            tick={{ fontSize: axisStyle.tickFontSize }}
          />
          {zKey ? (
            <ZAxis
              type="number"
              dataKey={zKey}
              range={zAxisRange}
              name={resolvedZAxisLabel || zKey}
            />
          ) : null}
          {referenceLineEntries.map((line) => (
            <ReferenceLine
              key={line.key}
              y={line.value}
              {...resolveVizSemanticReferenceLineProps({ color: line.color })}
              label={resolveVizReferenceLabelProps({
                label: line.label,
                color: line.color,
              })}
            />
          ))}
          <ChartTooltip
            cursor={resolveVizNeutralGuideLineProps()}
            content={
              <ScatterTooltipContent
                fieldMetaMap={metaByName}
                identity={tooltipIdentity}
                metricEntries={tooltipMetricEntries}
                formatMetricValue={formatTooltipValue}
                i18n={i18n}
              />
            }
          />
          {effectiveSeries.map((entry, index) => {
            const points = groupedPoints.get(entry.key) ?? [];
            const fillColor =
              chartConfig[entry.key]?.color ??
              resolveVizSeriesColor({
                index,
                color: entry.color,
                paletteMode: resolvedPaletteMode,
              });
            return (
              <Scatter
                key={entry.key}
                name={resolveDisplayLabel(
                  seriesLabels?.[entry.key],
                  entry.label,
                  entry.key,
                )}
                data={points}
                fill={fillColor}
                fillOpacity={0.84}
                stroke="hsl(var(--background))"
                strokeOpacity={0.92}
                strokeWidth={1.5}
                shape={(props: unknown) => {
                  const point = parseScatterShapePoint(props);
                  const payload = point
                    ? resolveScatterShapePayload(point)
                    : null;
                  const isHovered =
                    payload !== null &&
                    hoveredPointKey?.seriesValue === entry.key &&
                    hoveredPointKey.payload === payload;

                  return isHovered
                    ? renderScatterHoveredPointShape(props)
                    : renderScatterPointShape(props);
                }}
                onMouseEnter={createPointMouseEnterHandler(entry.key)}
                onMouseLeave={handlePointMouseLeave}
                isAnimationActive={false}
              />
            );
          })}
        </ScatterChart>
      </ChartContainer>
    </div>
  );
}
